/**
 * Team caps, as the hierarchy they are (#770).
 *
 * Contributions STACK down the ancestry — `resolveCaps` sums a member's teams
 * including every ancestor — so a parent's contribution also funds every member
 * of every sub-team. The flat table this replaced put root teams and sub-teams
 * side by side in no order, hiding the structure that produces the very number
 * an admin is here to set.
 *
 * Rendering only. The draft, the dirty set and Save stay in `BudgetsEditor`:
 * a row collapsed out of sight is still in the draft, so the page Save commits
 * it regardless — and `hiddenSubtreeHint` makes the collapsed parent SAY so.
 *
 * Layout: indent is a fixed 16px per level (the server caps nesting at 10, so
 * at most 144px); the Team column is sticky on the left and truncates long
 * names with a tooltip; the container scrolls horizontally on a narrow screen
 * instead of squeezing the name. A collapsed row's hints sit on their OWN line
 * under the name and wrap: inline they widened the Team column past a desktop
 * container and pushed the Spend column behind the scroll (seen in the running
 * app, 2026-09-23).
 */
import { useMemo, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { CapInput } from '@papan/core-ui/ui'
import type { BudgetCap } from '@papan/types'
import type { TeamUsageRow } from '../../client/budget'
import { buildTeamTree, capKey, hiddenSubtreeHint, isTeamExpanded, stagedCap, visibleTeamRows, type CapDraft, type TeamExpansion } from './logic'
import { MoneyText, type MoneyFmt } from './bits'
import { ChevronRightGlyph } from '../storage/shared'

const INDENT_PX = 16

export function TeamCapsTable({
  teams,
  draft,
  dirtyKeys,
  onStage,
  disabled,
  currencyCode,
  draftKeyBase,
  displayHint,
  money,
}: {
  teams: TeamUsageRow[]
  draft: CapDraft
  /** `BudgetsEditor`'s dirty set — measured against the server, so a value
   *  typed and typed back is not "unsaved". */
  dirtyKeys: readonly string[]
  onStage: (key: string, value: BudgetCap) => void
  disabled: boolean
  currencyCode: string
  /** #897 — prefix for each field's typed-text draft key (kept across a layout swap). */
  draftKeyBase?: string
  displayHint?: (micro: number) => string
  money: MoneyFmt
}) {
  const { t } = useTranslation('core-ui')
  const rows = useMemo(() => buildTeamTree(teams), [teams])
  const [toggled, setToggled] = useState<TeamExpansion>({})
  const visible = visibleTeamRows(rows, toggled)
  const dirty = useMemo(() => new Set(dirtyKeys), [dirtyKeys])
  const capById = useMemo(() => new Map(teams.map((tm) => [tm.id, tm.cap])), [teams])
  const shownCap = (id: string): BudgetCap | undefined => {
    const server = capById.get(id)
    return server === undefined ? undefined : stagedCap(draft, capKey('team', id), server)
  }

  return (
    <div className="themed-bg-secondary rounded-lg overflow-x-auto" data-testid="team-caps-table">
      <table className="w-full min-w-[34rem] text-sm">
        <thead>
          <tr className="border-b themed-border">
            <th className="sticky left-0 z-10 themed-bg-secondary text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.team')}</th>
            <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.contribution')}</th>
            {/* Named for what it counts: `used` already rolls a sub-team
                member's spend up into every ancestor, so a parent's figure
                includes its sub-teams'. A bare "Spend" read as this team only. */}
            <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.teamCaps.spendInclSubteams')}</th>
          </tr>
        </thead>
        <tbody>
          {teams.length === 0 && (
            <tr><td className="px-4 py-3 themed-text-muted text-sm" colSpan={3}>{t('settings.budget.noTeams')}</td></tr>
          )}
          {visible.map((row) => {
            const tm = row.team
            const key = capKey('team', tm.id)
            const expanded = isTeamExpanded(row, toggled)
            const hint = row.childCount > 0 && !expanded
              ? hiddenSubtreeHint(row, (id) => dirty.has(capKey('team', id)), shownCap)
              : null
            return (
              <tr key={tm.id} className="border-b themed-border last:border-b-0" data-team-id={tm.id} data-depth={row.depth}>
                <td className="sticky left-0 z-10 themed-bg-secondary px-4 py-2.5">
                  <div className="min-w-0" style={{ paddingLeft: row.depth * INDENT_PX }}>
                    <div className="flex items-center gap-1.5 min-w-0">
                      {row.childCount > 0 ? (
                        <button
                          type="button"
                          onClick={() => setToggled((s) => ({ ...s, [tm.id]: !expanded }))}
                          aria-expanded={expanded}
                          aria-label={t(expanded ? 'settings.budget.teamCaps.collapse' : 'settings.budget.teamCaps.expand', { name: tm.name })}
                          className="shrink-0 themed-text-muted hover:themed-text rounded outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                        >
                          <span
                            aria-hidden="true"
                            className="block transition-transform duration-200 motion-reduce:transition-none"
                            style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
                          >
                            <ChevronRightGlyph size={14} />
                          </span>
                        </button>
                      ) : (
                        // Spacer keeps leaf names aligned with their siblings'.
                        <span aria-hidden="true" className="shrink-0 w-[14px]" />
                      )}
                      <span className="themed-text truncate max-w-[12rem]" title={tm.name}>{tm.name}</span>
                    </div>
                    {hint && (
                      // Own line, aligned under the name (past the 14px chevron
                      // + 6px gap), and allowed to wrap — see the header note.
                      <div className="flex flex-wrap items-center gap-1 mt-0.5 pl-[20px]" data-testid="team-hidden-hint">
                        <span className="text-[10px] themed-text-muted whitespace-nowrap">
                          {t('settings.budget.teamCaps.hiddenCount', { count: hint.hidden })}
                        </span>
                        {hint.unsaved > 0 && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 whitespace-nowrap"
                            data-testid="team-hidden-unsaved"
                          >
                            {t('settings.budget.teamCaps.hiddenUnsaved', { count: hint.unsaved })}
                          </span>
                        )}
                        {hint.funded > 0 && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 whitespace-nowrap"
                            data-testid="team-hidden-funded"
                          >
                            {t('settings.budget.teamCaps.hiddenFunded', { count: hint.funded })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                {/* Contribution, not a ceiling: 0 adds nothing to the pool
                    and blank is the same as 0 here, so no "blocked" chip. */}
                <td className="px-4 py-2.5">
                  <CapInput
                    value={stagedCap(draft, key, tm.cap)}
                    draftKey={draftKeyBase ? `${draftKeyBase}:${key}` : undefined}
                    currencyCode={currencyCode}
                    displayHint={displayHint}
                    zeroIsBlock={false}
                    nullLabel={t('settings.budget.contributesNothing')}
                    disabled={disabled}
                    ariaLabel={`${tm.name} — ${t('settings.budget.col.contribution')}`}
                    onCommit={(v) => onStage(key, v)}
                  />
                </td>
                <td className="px-4 py-2.5"><MoneyText micro={tm.used} money={money} className="themed-text-muted text-xs" /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
